import { CommonModule, CurrencyPipe } from '@angular/common';
import { APP_ID, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { LinkField } from '@prismicio/client';
import { DynamicHooksComponent } from 'ngx-dynamic-hooks';
import { AnalyticsService } from 'analytics';
import { UserService } from 'auth-data-access';
import { BrandService } from 'brand';
import { SvgComponent } from 'icon';
import { LanguageService } from 'language';
import { LinkComponent } from 'link';
import { ProductListItem, ProductService } from 'product-shared';
import { PromotionBannerComponent } from 'promotion-banner';
import { ProductCardChipsComponent, ProductCardComponent, ProductCardPriceComponent } from 'sakura-ui';
import { TranslatePipe } from 'translate';
import { App, createLinkFromString, CurrencyFormatterPipe } from 'utils';
import { ProductBoxBase } from '../product-box-base';
import { ProductBoxFeaturesComponent } from '../tables/product-features/product-features.component';

@Component({
  selector: 'lib-product-box',
  templateUrl: './product-box.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    SvgComponent,
    LinkComponent,
    PromotionBannerComponent,
    DynamicHooksComponent,
    ProductBoxFeaturesComponent,
    TranslatePipe,
    ProductCardComponent,
    ProductCardChipsComponent,
    ProductCardPriceComponent,
    CurrencyFormatterPipe,
    CurrencyPipe,
  ],
})
export class ProductBoxComponent extends ProductBoxBase {
  private analytics = inject(AnalyticsService);
  private productService = inject(ProductService);
  private appId = inject(APP_ID, { optional: true });
  private languageService = inject(LanguageService);
  private userService = inject(UserService);
  public brand = inject(BrandService);
  private readonly userType = toSignal(this.userService.getUserType());
  public productItem = input<ProductListItem>(null);
  public countdownDate = input<string>();
  public product = computed(() => this.productItem()?.product);
  public promotion = computed(() => this.productItem()?.promotion);
  public lightMode = input(false);
  public isNavProduct = input(false);
  public showLearnMore = input(true);
  public typeOfPageShownOn = input('');
  public ctaCustomLabel = input('');
  public ctaCustomClasses = input('');
  public ctaCustomLink = input<LinkField>();
  public cssClass = input('');
  public productDetailsLink = computed<LinkField>(() => this.productService.productDetailsLink(this.product()));
  public ctaConfig = computed(() => {
    this.languageService.currentLanguage();

    const { special_product_type, alternative_link, product_type, product_details_link } = this.product();
    const hfcOrFiber = ['hfc', 'fiber'].includes(product_type);
    const hfcOrFiberInsideNavigation = hfcOrFiber && this.lightMode();
    const hfcOrFiberInsideLineCheckResults = hfcOrFiber && !!this.typeOfPageShownOn();
    const anyProductInsideSpecialDetailPageResults = this.typeOfPageShownOn() === 'special product detail page';
    const specialProductInsideHomePageLineCheckResults =
      special_product_type && this.typeOfPageShownOn() === 'home page'; // HOME MAX, HOME MAX TV

    if (this.ctaCustomLink()) {
      return { link: this.ctaCustomLink() };
    } else if (hfcOrFiberInsideNavigation) {
      return { link: product_details_link };
    } else if (
      this.appId === App.website &&
      (specialProductInsideHomePageLineCheckResults ||
        (alternative_link && !(hfcOrFiberInsideLineCheckResults || anyProductInsideSpecialDetailPageResults)))
    ) {
      return { link: createLinkFromString(alternative_link) as LinkField };
    } else {
      // Default checkout path logic
      return this.productService.buildCheckoutPath(this.product(), this.promotion(), this.productItem()?.lineCheckId);
    }
  });
  public queryParams = computed(() => this.ctaConfig().queryParams);
  public ctaLink = computed(() => this.ctaConfig().link);
  public ctaStyling = computed(() => {
    let label;
    if (this.ctaCustomLabel()) {
      label = this.ctaCustomLabel();
    } else {
      if ((this.product().product_type === 'hfc' || this.product().product_type === 'fiber') && this.lightMode()) {
        label = this.product().product_details_link_label;
      } else {
        label = this.product().cta_button_label || this.translateService.getTranslation(['checkoutButton']);
      }
    }

    let classes;
    if (this.ctaCustomClasses()) {
      classes = this.ctaCustomClasses();
    } else {
      classes = this.promotion() && this.hasDiscountedCost() ? 'featured pulse' : '';
    }

    return {
      label,
      classes,
    };
  });
  public contractDuration = computed(() => {
    if (!this.promotion()?.lockinLengths?.length && !this.product()?.lockin_length?.length) {
      return;
    }
    return this.productService.contractDuration(this.promotion()?.lockinLengths, this.product()?.lockin_length);
  });
  public discountPercentage = computed(() =>
    this.productService.getDiscountPercentage(this.product()?.monthly_cost, this.promotion()?.discountAmount)
  );
  public chipsLabelsKeys = computed(() =>
    this.productService.getChipsLabelsKeys(
      !!this.promotion(),
      this.hasDiscountedCost(),
      this.activationFee(),
      this.contractDuration()
    )
  );

  public onProductBoxClick() {
    if (this.productService.isLinkToCheckout(this.ctaLink())) {
      this.analytics.addECommerceEvent('add_to_cart', this.product(), this.promotion(), undefined, this.userType());
    }
    if (this.product().alternative_link === '/tv-free-signup') {
      const data = {
        event: 'gtm visitor interaction',
        interaction_name: 'free tv click',
        free_tv_step: 'from tv-free to signup page',
      };
      this.analytics.customEvent(data);
    }

    if (this.isNavProduct()) {
      this.analytics.addToDataLayer({
        event: 'gtm visitor interaction',
        interaction_name: 'mobile nav link click',
        mobile_nav_link_name: `offer - ${this.product().name}`,
      });
    }
  }
}
